import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { providerLabel, type ModelProvider } from "@/app/lib/modelAvailability";
import { useTranslation } from "@/app/i18n/LocaleContext";

interface Props {
    open: boolean;
    onClose: () => void;
    provider: ModelProvider | null;
    /** Optional override for the body sentence. */
    message?: string;
}

export function ApiKeyMissingModal({ open, onClose, provider, message }: Props) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    if (!open) return null;

    const providerName = provider ? providerLabel(provider) : "this provider";
    const body =
        message ??
        `You haven't added a ${providerName} API key yet. Add one in your account settings to use this model.`;

    const handleGoToAccount = () => {
        onClose();
        navigate("/account/models");
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/10 backdrop-blur-xs"
            onClick={onClose}
        >
            <div
                className="w-full max-w-md rounded-2xl bg-white shadow-2xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-2">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <h2 className="text-base font-medium text-gray-900">
                            {t("modals", "apiKeyMissingTitle")}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-5 pb-2 pt-1">
                    <p className="text-sm text-gray-600 leading-relaxed">
                        {body}
                    </p>
                </div>

                <div className="flex justify-end gap-2 px-5 pb-5 pt-3">
                    <button
                        onClick={onClose}
                        className="rounded-lg px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
                    >
                        {t("common", "cancel")}
                    </button>
                    <button
                        onClick={handleGoToAccount}
                        className="rounded-lg bg-gray-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-gray-700"
                    >
                        {t("sidebar", "accountSettings")}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
